import type { ReactNode } from 'react'

import { getListName } from 'analytics/enhanced-ecommerce'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

import CruisePageCruiseLinesSection from './CruisePageCruiseLinesSection/CruisePageCruiseLinesSection'
import CruisePageDealsTileCarousel from './CruisePageDealsTileCarousel'
import CruisePageDepartureFromCarousel from './CruisePageDepartureFromCarousel/CruisePageDepartureFromCarousel'
import CruisePageDeparturesSection from './CruisePageDeparturesSection'
import CruisePageDestinationsSection from './CruisePageDestinationsSection'
import CruisePageCruiseTourCarousel from './CruisePageCruiseTourCarousel'
import CruisePageExpeditionCruisesCarousel from './CruisePageExpeditionCruisesCarousel'
import CruisePageLongHaulOceanCruisesCarousel from './CruisePageLongHaulOceanCruisesCarousel'
import CruisePageLuxuryPremiumCruisesCarousel from './CruisePageLuxuryPremiumCruisesCarousel'
import CruisePageLuxPlusOffersCarousel from './CruisePageLuxPlusOffersCarousel'
import CruisePageRiverCruisesCarousel from './CruisePageRiverCruisesCarousel'
import CruisePageRoyalCruisesCarousel from './CruisePageRoyalCruisesCarousel'
import CruisePageShortTermCruisesCarousel from './CruisePageShortTermCruisesCarousel'
import CruisePageSpecialOffersCarousel from './CruisePageSpecialOffersCarousel'
import CruisePageTopOffersCarousel from './CruisePageTopOffersCarousel'
import { CruisePageCarouselSection } from './useCruisePageContentOrder'

const CONTENT_SECTION_LIST_NAMES: Record<CruisePageCarouselSection, string> = {
  [CruisePageCarouselSection.Deals]: 'Hero Carousel',
  [CruisePageCarouselSection.TopOffers]: 'Todays Top Cruises',
  [CruisePageCarouselSection.LuxPlusOffers]: 'LuxPlus Offers',
  [CruisePageCarouselSection.CruiseLines]: 'Featured Cruise Lines',
  [CruisePageCarouselSection.Destinations]: 'Popular cruise destinations',
  [CruisePageCarouselSection.DepartingFrom]: 'Departing From',
  [CruisePageCarouselSection.RoyalCaribbean]: 'Royal Caribbean Hot Sailings',
  [CruisePageCarouselSection.Departures]: 'Top Ports to Start Your Adventure',
  [CruisePageCarouselSection.ShortTerm]: 'Short Term Cruises',
  [CruisePageCarouselSection.LongHaulOcean]: 'Long-haul Ocean Cruises',
  [CruisePageCarouselSection.LuxuryPremium]: 'Luxury & Premium Cruises',
  [CruisePageCarouselSection.River]: 'River Cruises',
  [CruisePageCarouselSection.BestValue]: 'Best Value',
  [CruisePageCarouselSection.Expedition]: 'Expedition Cruises',
  [CruisePageCarouselSection.CruiseTour]: 'Cruise Tour',
}

interface Props {
  contentName: CruisePageCarouselSection
}

export default function CruisePageContentSection({ contentName }: Props) {
  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', CONTENT_SECTION_LIST_NAMES[contentName]),
  )

  let content: ReactNode

  switch (contentName) {
    case CruisePageCarouselSection.Deals:
      content = (
        <>
          <ScrollAnchor id="deals-carousel" />
          <CruisePageDealsTileCarousel />
        </>
      )
      break

    case CruisePageCarouselSection.TopOffers:
      content = <CruisePageTopOffersCarousel />
      break

    case CruisePageCarouselSection.LuxPlusOffers:
      content = <CruisePageLuxPlusOffersCarousel />
      break

    case CruisePageCarouselSection.CruiseLines:
      content = <CruisePageCruiseLinesSection />
      break

    case CruisePageCarouselSection.Destinations:
      content = <CruisePageDestinationsSection />
      break

    case CruisePageCarouselSection.DepartingFrom:
      content = <CruisePageDepartureFromCarousel />
      break

    case CruisePageCarouselSection.RoyalCaribbean:
      content = <CruisePageRoyalCruisesCarousel />
      break

    case CruisePageCarouselSection.Departures:
      content = <CruisePageDeparturesSection />
      break

    case CruisePageCarouselSection.ShortTerm:
      content = <CruisePageShortTermCruisesCarousel />
      break

    case CruisePageCarouselSection.LongHaulOcean:
      content = <CruisePageLongHaulOceanCruisesCarousel />
      break

    case CruisePageCarouselSection.LuxuryPremium:
      content = <CruisePageLuxuryPremiumCruisesCarousel />
      break

    case CruisePageCarouselSection.River:
      content = <CruisePageRiverCruisesCarousel />
      break

    case CruisePageCarouselSection.BestValue:
      content = <CruisePageSpecialOffersCarousel />
      break

    case CruisePageCarouselSection.Expedition:
      content = <CruisePageExpeditionCruisesCarousel />
      break

    case CruisePageCarouselSection.CruiseTour:
      content = <CruisePageCruiseTourCarousel />
      break
  }

  return (
    <OfferListEventsProvider tracking={tracking}>
      {content}
    </OfferListEventsProvider>
  )
}
